import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleDot,
  faPlay,
  faStop,
  faVideo,
} from "@fortawesome/free-solid-svg-icons";

import {
  EObsOutputAction,
  EObsOutputKind,
  EObsOutputState,
  ICouncilStreamState,
} from "dto/Plugin/councilStream";

import styles from "./styles.module.scss";

// Panel transmisji w „Prowadzeniu obrad" — odpowiednik zakładki „Transmisja wideo"
// z okna moderatora w Magistracie (ModeratorModal.xaml, DataTemplate x:Key="4").
//
// Odwzorowane 1:1: dwa stany panelu (przed połączeniem i po), statystyki transmisji,
// lista scen z rolą „widok domyślny" i „widok głosowania", przełączniki transmisji
// i nagrywania oraz przyciski predefiniowanych widoków kamery.
//
// Różnica: role scen zapisują się w ustawieniach urzędu, a nie w ustawieniach
// użytkownika. W Magistracie siedziały w RemoteSettings, czyli per stanowisko —
// przy zmianie realizatora konfigurację trzeba było ustawić od nowa.

const streamStateLabel = (state: EObsOutputState) => {
  switch (state) {
    case EObsOutputState.Starting:
      return "Rozpoczynanie transmisji";
    case EObsOutputState.Started:
      return "Transmisja aktywna";
    case EObsOutputState.Stopping:
      return "Zatrzymywanie transmisji";
    default:
      return "Transmisja nieaktywna";
  }
};

const recordStateLabel = (state: EObsOutputState) => {
  switch (state) {
    case EObsOutputState.Starting:
      return "Rozpoczynanie nagrywania";
    case EObsOutputState.Started:
      return "Nagrywanie aktywne";
    case EObsOutputState.Stopping:
      return "Zatrzymywanie nagrywania";
    default:
      return "Nagrywanie nieaktywne";
  }
};

const inTransition = (state: EObsOutputState) =>
  state === EObsOutputState.Starting || state === EObsOutputState.Stopping;

const duration = (seconds: number) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (v: number) => String(v).padStart(2, "0");

  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

interface Props {
  state: ICouncilStreamState | undefined;
  /** Wtyczka nie odpowiedziała — na tym stanowisku nie da się sterować transmisją. */
  pluginMissing: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  onRunObs: () => void;
  onSetOutput: (kind: EObsOutputKind, action: EObsOutputAction) => void;
  /** Zapis roli sceny trwa — oba znaczniki są wtedy nieczynne. */
  savingSceneRole?: boolean;
  onSelectScene: (name: string) => void;
  onSetSceneRole: (role: "default" | "voting", name: string) => void;
  onGotoPreset: (cameraId: string, preset: number) => void;
}

const StreamPanel = ({
  state,
  pluginMissing,
  onConnect,
  onDisconnect,
  onRunObs,
  onSetOutput,
  onSelectScene,
  onSetSceneRole,
  onGotoPreset,
  savingSceneRole = false,
}: Props) => {
  const cameras = state?.Cameras ?? [];
  const hasPresets = cameras.some((c) => c.Presets.length > 0);

  if (pluginMissing) {
    return (
      <div className={styles.streamBox}>
        <div className={styles.streamHeader}>
          <FontAwesomeIcon icon={faVideo} />
          <span className={styles.streamTitle}>Transmisja</span>
        </div>
        <div className={styles.conductHint}>
          Sterowanie OBS Studio i kamerami działa przez wtyczkę Nota — uruchom ją na
          tym komputerze. Obrady można prowadzić bez niej, ale bez napisów, sterowania
          transmisją i najazdów kamery.
        </div>
      </div>
    );
  }

  if (!state) return null;

  const streaming = state.StreamState === EObsOutputState.Started;

  return (
    <div className={styles.streamBox}>
      <div className={styles.streamHeader}>
        <FontAwesomeIcon icon={faVideo} />
        <span className={styles.streamTitle}>Transmisja</span>

        {streaming && (
          <span className={styles.streamLive}>
            <FontAwesomeIcon icon={faCircleDot} />
            NA ŻYWO
          </span>
        )}

        <div className={styles.spacer} />

        {state.Connected ? (
          <button type="button" className={styles.conductAction} onClick={onDisconnect}>
            Rozłącz OBS
          </button>
        ) : (
          <>
            {!state.ObsRunning && (
              <button type="button" className={styles.conductAction} onClick={onRunObs}>
                Uruchom OBS Studio
              </button>
            )}
            <button
              type="button"
              className={`${styles.conductAction} ${styles.conductActionPrimary}`}
              onClick={onConnect}
            >
              Połącz z OBS
            </button>
          </>
        )}
      </div>

      {!!state.Error && <div className={styles.conductHint}>{state.Error}</div>}

      {state.Connected && (
        <>
          <div className={styles.streamStats}>
            <span>{streamStateLabel(state.StreamState)}</span>
            <span>{recordStateLabel(state.RecordState)}</span>
            {state.StreamState !== EObsOutputState.Stopped && (
              <>
                <span>czas {duration(state.StreamSeconds)}</span>
                <span>{state.KbitsPerSec} kbit/s</span>
                <span>{state.Fps} kl./s</span>
                <span>utracone klatki: {state.DroppedFrames}</span>
                {state.CongestionPercent > 20 && (
                  <span className={styles.streamStrain}>
                    obciążenie łącza {state.CongestionPercent}%
                  </span>
                )}
              </>
            )}
          </div>

          <div className={styles.streamActions}>
            <button
              type="button"
              className={`${styles.conductAction} ${styles.conductActionPrimary}`}
              disabled={inTransition(state.StreamState)}
              onClick={() =>
                onSetOutput(
                  EObsOutputKind.Stream,
                  streaming ? EObsOutputAction.Stop : EObsOutputAction.Start,
                )
              }
            >
              <FontAwesomeIcon icon={streaming ? faStop : faPlay} />
              {streaming ? "Zakończ transmisję" : "Rozpocznij transmisję"}
            </button>

            <button
              type="button"
              className={styles.conductAction}
              disabled={inTransition(state.RecordState)}
              onClick={() =>
                onSetOutput(
                  EObsOutputKind.Record,
                  state.RecordState === EObsOutputState.Started
                    ? EObsOutputAction.Stop
                    : EObsOutputAction.Start,
                )
              }
            >
              {state.RecordState === EObsOutputState.Started
                ? "Zakończ nagrywanie"
                : "Rozpocznij nagrywanie"}
            </button>
          </div>

          {!!state.Scenes.length && (
            <div className={styles.sceneList}>
              {state.Scenes.map((scene) => {
                const isCurrent = scene === state.CurrentScene;
                const isDefault = scene === state.DefaultScene;
                const isVoting = scene === state.VotingScene;

                return (
                  <div
                    key={scene}
                    className={`${styles.sceneRow} ${isCurrent ? styles.sceneRowActive : ""}`}
                  >
                    <button
                      type="button"
                      className={styles.sceneName}
                      title="Przełącz OBS na tę scenę"
                      onClick={() => onSelectScene(scene)}
                    >
                      {scene}
                    </button>

                    {/*
                      Oba znaczniki są nieczynne w trakcie zapisu: każdy z nich przepisuje
                      całe ustawienia urzędu, więc dwa kliknięcia pod rząd wysłałyby drugie
                      żądanie ze stanem sprzed pierwszego i cicho cofnęłyby pierwszą rolę.
                    */}
                    <button
                      type="button"
                      className={`${styles.sceneRole} ${isDefault ? styles.sceneRoleOn : ""}`}
                      disabled={savingSceneRole}
                      title="Widok domyślny — na niego OBS wraca po głosowaniu"
                      onClick={() => onSetSceneRole("default", isDefault ? "" : scene)}
                    >
                      domyślna
                    </button>

                    <button
                      type="button"
                      className={`${styles.sceneRole} ${isVoting ? styles.sceneRoleOn : ""}`}
                      disabled={savingSceneRole}
                      title="Widok głosowania — na niego OBS przechodzi na czas głosowania"
                      onClick={() => onSetSceneRole("voting", isVoting ? "" : scene)}
                    >
                      głosowania
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {state.CamerasEnabled && hasPresets && (
        <div className={styles.presetRow}>
          <span className={styles.fieldLabel}>Widoki kamery:</span>

          {cameras.map((camera) =>
            camera.Presets.map((preset) => (
              <button
                key={preset.Id}
                type="button"
                className={styles.conductAction}
                title={
                  cameras.length > 1 ? `${camera.Label} — ${preset.Name}` : undefined
                }
                onClick={() => onGotoPreset(camera.Id, preset.Number)}
              >
                {preset.Name}
              </button>
            )),
          )}
        </div>
      )}
    </div>
  );
};

export default StreamPanel;
